import { useMutation, useQuery } from '@apollo/client';
import {
	Button,
	Chip,
	Dialog,
	DialogProps,
	Icon,
	List,
	Table,
	TableColumnType,
	TableInstance,
} from '@elipssolution/harfang';
import { mdiAlertCircle, mdiEarth, mdiEmailArrowRight, mdiLock } from '@mdi/js';
import { Alert, CircularProgress, ListItemText, Stack, styled, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useSession as useNextAuthSession } from 'next-auth/react';
import { useCallback, useMemo } from 'react';

import SignersAvatarGroup from './SignersAvatarGroup';
import { useConfirmationDialogContext } from '../../../components/ConfirmationDialogProvider';
import { useSession } from '../../../src/components/session/SessionContext';
import { getFileBlob, openFileInNewTab } from '../../../src/utils/file';
import { generateErrorInformations } from '../../../utils/errorHandler';
import { ERROR_ANIMATION_DELAY, SUCCESS_ANIMATION_DELAY } from '../../../utils/statusAnimationDelay';
import {
	CANCEL_PROCEDURE,
	CancelProcedureType,
	FETCH_PROCEDURE,
	FetchProcedureType,
	REMIND_PROCEDURE_AWAITED_SIGNERS,
	RemindProcedureAwaitedSignersType,
} from '../api/procedure';
import { ProcedureDocumentType } from '../types/document';
import { ProcedureStatusEnum, ProcedureType } from '../types/procedure';
import { SignerType } from '../types/signer';

const StyledListItemText = styled(ListItemText)({
	textAlign: 'left',
});

const dialogStatusMapper: Map<ProcedureStatusEnum, { label: string; color: string }> = new Map([
	[ProcedureStatusEnum.CANCELED, { label: 'Annulée', color: 'warning' }],
	[ProcedureStatusEnum.DECLINED, { label: 'Rejetée', color: 'error' }],
	[ProcedureStatusEnum.DONE, { label: 'Terminée', color: 'success' }],
	[ProcedureStatusEnum.DRAFT, { label: 'Brouillon', color: 'default' }],
	[ProcedureStatusEnum.ONGOING, { label: 'En cours', color: 'primary' }],
]);

const documentColumns: TableColumnType<ProcedureDocumentType>[] = [
	{
		field: 'filename',
		key: 'filename',
		title: 'Nom',
		width: 200,
	},
	{
		field: 'signers',
		key: 'signers',
		title: 'Signataires',
		render: ({ signers }) => <SignersAvatarGroup signers={signers} />,
		width: 100,
	},
];

const signersRowRenderer = ({ firstName, lastName, email }: SignerType) => (
	<Stack flexDirection="row" gap={1} alignItems="center" paddingRight={1} paddingLeft={1}>
		<StyledListItemText
			primary={`${firstName}
			${lastName}`}
			secondary={email}
		/>
	</Stack>
);

const ProcedureStatusChip = ({ status }: { status: ProcedureStatusEnum }) => {
	const { color, label } = dialogStatusMapper.get(status) ?? {};

	return <Chip color={color} label={label} />;
};

const LoadingWrapper = styled('div')(({ theme: { spacing } }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: spacing(2),
}));

const ErrorWrapper = styled('div')(({ theme: { spacing, palette, shape } }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	padding: spacing(2),
	gap: spacing(2),
	color: palette.error.main,
	backgroundColor: `${palette.error.main}1A`,
	border: `1px solid ${palette.error.main}`,
	borderRadius: shape.borderRadius,
	width: 'fit-content',
	margin: 'auto',
}));

const SignersAndDocumentsWrapper = styled('div')(({ theme: { spacing } }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	gap: spacing(3),

	height: 400,
}));

type ProcedureConsultationDialogProps = {
	procedureId?: ProcedureType['id'];
	tableInstance?: TableInstance;
	onClose: () => void;
};

const ProcedureConsultationDialog = ({ procedureId, tableInstance, onClose }: ProcedureConsultationDialogProps) => {
	const { customerFile: sessionCustomerFile } = useSession();
	const { confirmAction } = useConfirmationDialogContext();

	const { data: sessionData } = useNextAuthSession();
	const { access_token } = sessionData ?? {};

	const {
		data: procedureData,
		error: fetchProcedureError,
		loading: isFetchProcedureLoading,
		refetch: refetchProcedure,
	} = useQuery<FetchProcedureType>(FETCH_PROCEDURE, {
		variables: {
			signProcedureId: procedureId,
		},
		skip: !procedureId,
	});

	const fetchProcedureErrorMessage = fetchProcedureError
		? generateErrorInformations({ error: fetchProcedureError, resource: 'sign_procedure' }).message
		: undefined;

	const {
		name,
		isPrivate,
		status,
		comment,
		signers = [],
		documents = [],
		lastReminderAt,
		nextReminderAt,
		isAutomaticRemindersEnabled,
	} = procedureData?.sign_procedure || {};

	const proceduresSignersDataSource = useCallback(
		async (
			limit: number,
			offset: number,
			search?: string,
		): Promise<{
			count: number;
			items: SignerType[];
		}> => {
			const displayedSigners = signers.filter((contact) => {
				const fullName = `${contact.firstName} ${contact.lastName}`
					.toLowerCase()
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '');

				return search
					? fullName.includes(
							search
								.toLowerCase()
								.normalize('NFD')
								.replace(/[\u0300-\u036f]/g, ''),
					  )
					: signers;
			});

			return Promise.resolve({
				items: displayedSigners.slice(offset, offset + limit),
				count: displayedSigners.length,
			});
		},
		[signers],
	);

	const proceduresDocumentsDataSource = useCallback(
		async (): Promise<{
			items: ProcedureDocumentType[];
			count: number;
		}> =>
			Promise.resolve({
				items: documents,
				count: documents.length,
			}),
		[documents],
	);

	const dialogTitle = useMemo(
		() =>
			!isFetchProcedureLoading &&
			!fetchProcedureError && (
				<Stack flexDirection="row" gap={2} marginRight={2} alignItems="center">
					<Icon path={isPrivate ? mdiLock : mdiEarth} />
					{name}
					{status && <ProcedureStatusChip status={status} />}
				</Stack>
			),
		[isFetchProcedureLoading, fetchProcedureError, isPrivate, name, status],
	);

	const [
		cancelProcedure,
		{ data: cancelProcedureMutationData, error: cancelProcedureMutationError, reset: resetCancelProcedureMutation },
	] = useMutation<CancelProcedureType>(CANCEL_PROCEDURE);

	const cancelProcedureErrorMessage = useMemo(() => {
		if (!cancelProcedureMutationError) return undefined;

		const { message } = generateErrorInformations({ error: cancelProcedureMutationError });

		setTimeout(() => {
			resetCancelProcedureMutation();

			return undefined;
		}, ERROR_ANIMATION_DELAY);

		return message;
	}, [cancelProcedureMutationError, resetCancelProcedureMutation]);

	const [
		remindProcedureAwaitedSigners,
		{
			data: remindProcedureAwaitedSignersMutationData,
			error: remindProcedureAwaitedSignersMutationError,
			reset: resetRemindProcedureAwaitedSignersMutation,
		},
	] = useMutation<RemindProcedureAwaitedSignersType>(REMIND_PROCEDURE_AWAITED_SIGNERS);

	const remindProcedureAwaitedSignersErrorMessage = useMemo(() => {
		if (!remindProcedureAwaitedSignersMutationError) return undefined;

		const { message } = generateErrorInformations({ error: remindProcedureAwaitedSignersMutationError });

		setTimeout(() => {
			resetRemindProcedureAwaitedSignersMutation();

			return undefined;
		}, ERROR_ANIMATION_DELAY);

		return message;
	}, [remindProcedureAwaitedSignersMutationError, resetRemindProcedureAwaitedSignersMutation]);

	const handleCloseDialog = useCallback(() => {
		resetCancelProcedureMutation();
		resetRemindProcedureAwaitedSignersMutation();
		onClose();
	}, [onClose, resetCancelProcedureMutation, resetRemindProcedureAwaitedSignersMutation]);

	const extraInfos = useMemo(
		(): DialogProps['extraInfos'] => [
			<Button key="cancel-button" onClick={handleCloseDialog}>
				Annuler
			</Button>,
		],
		[handleCloseDialog],
	);

	const applyCancelProcedure = async () => {
		const result = await cancelProcedure({
			variables: {
				signCancelProcedureId: procedureId,
			},
		});

		tableInstance?.reload();

		setTimeout(() => {
			refetchProcedure()
				.then(resetCancelProcedureMutation)
				.catch((error) => {
					throw error;
				});
		}, SUCCESS_ANIMATION_DELAY);

		return result;
	};

	const handleCancelProcedureButtonClick = async () => {
		if (!name) return undefined;

		const isCancelConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir annuler la procédure ${name} ?`,
			confirmButtonDetails: {
				label: 'Annuler la procédure',
			},
		});

		if (!isCancelConfirmed) return null;

		return applyCancelProcedure();
	};

	const applyRemindSigners = async () => {
		const result = await remindProcedureAwaitedSigners({
			variables: {
				signRemindProcedureAwaitedSignersId: procedureId,
			},
		});

		setTimeout(() => resetRemindProcedureAwaitedSignersMutation(), SUCCESS_ANIMATION_DELAY);

		return result;
	};

	const handleRemindSignersButtonClick = async () => {
		if (!name) return undefined;

		const isRemindConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir relancer les signataires de la procédure ${name} ?`,
			confirmButtonDetails: {
				label: 'Relancer les signataires',
				icon: <Icon path={mdiEmailArrowRight} />,
			},
		});

		if (!isRemindConfirmed) return null;

		return applyRemindSigners();
	};

	const actionsDialog = [
		...(status === ProcedureStatusEnum.ONGOING
			? [
					{
						id: 'cancelProcedure',
						disabled: isFetchProcedureLoading || !!fetchProcedureError,
						error: !!cancelProcedureErrorMessage,
						label: cancelProcedureMutationData ? 'Procédure annulée' : 'Annuler la procédure',
						persistentErrorMessage: cancelProcedureErrorMessage,
						variant: 'outlined',
						color: 'warning',
						onClick: handleCancelProcedureButtonClick,
					},
					{
						id: 'remindSigners',
						disabled: isFetchProcedureLoading || !!fetchProcedureError,
						error: !!remindProcedureAwaitedSignersErrorMessage,
						startIcon: <Icon path={mdiEmailArrowRight} />,
						label: remindProcedureAwaitedSignersMutationData ? 'Signataires relancés' : 'Relancer les signataires',
						persistentErrorMessage: remindProcedureAwaitedSignersErrorMessage,
						variant: 'outlined',
						color: 'inherit',
						onClick: handleRemindSignersButtonClick,
					},
			  ]
			: []),
	] as DialogProps['actionsDialog'];

	const fetchDocumentFile = useCallback(
		async ({
			documentId: id,
			procedureId: currentProcedureId,
			fileName,
		}: {
			documentId: string;
			procedureId?: ProcedureType['id'];
			fileName?: string;
		}) => {
			if (!currentProcedureId) throw new Error('ProcedureId is missing.');
			if (!fileName) throw new Error('FileName is missing.');
			const documentBlob = await getFileBlob({
				url: `/sign/documents?id=${id}&procedureId=${currentProcedureId}`,
				accessToken: access_token,
				selectedCustomerFileId: sessionCustomerFile?.id,
			});

			return new File([documentBlob], `${fileName}`, { type: 'application/pdf' });
		},
		[access_token, sessionCustomerFile?.id],
	);

	const handleVisualizeDocument = useCallback(
		async ({ id, filename }: ProcedureDocumentType) => {
			const documentToVisualize = await fetchDocumentFile({
				documentId: id,
				procedureId,
				fileName: filename,
			});
			openFileInNewTab(documentToVisualize);
		},
		[fetchDocumentFile, procedureId],
	);

	return (
		<Dialog
			title={dialogTitle}
			actionsDialog={actionsDialog}
			extraInfos={[extraInfos]}
			open={!!procedureId}
			onClose={handleCloseDialog}
			fullWidth
		>
			{isFetchProcedureLoading && (
				<LoadingWrapper>
					<CircularProgress size={36} color="inherit" />
					<Typography>Chargement en cours...</Typography>
				</LoadingWrapper>
			)}

			{fetchProcedureError && (
				<ErrorWrapper>
					<Icon size={36} path={mdiAlertCircle} color="error" />
					<Typography>{fetchProcedureErrorMessage}</Typography>
				</ErrorWrapper>
			)}

			{!isFetchProcedureLoading && !fetchProcedureError && (
				<Stack gap={3}>
					{comment && <Typography>{comment}</Typography>}
					<Stack gap={1}>
						<SignersAndDocumentsWrapper>
							<List<SignerType>
								searchPlaceholder="Signataires"
								style={{ flex: 1, marginTop: 24, marginBottom: 19 }}
								dataSource={proceduresSignersDataSource}
								rowRenderer={signersRowRenderer}
								isSearchEnabled
							/>

							<Table<ProcedureDocumentType>
								columns={documentColumns}
								onRowClick={(documentType) => {
									handleVisualizeDocument(documentType).catch(() => {
										throw new Error('An error occurred while visualizing the document.');
									});
								}}
								dataSource={proceduresDocumentsDataSource}
							/>
						</SignersAndDocumentsWrapper>
						{(lastReminderAt || (isAutomaticRemindersEnabled && nextReminderAt)) && (
							<Alert severity="info" sx={{ alignItems: 'center' }}>
								{lastReminderAt && (
									<Typography>
										La dernière relance des signataires était le{' '}
										<strong>{format(new Date(lastReminderAt), "dd/MM/yyyy 'à' HH:mm")}</strong>
									</Typography>
								)}
								{isAutomaticRemindersEnabled && nextReminderAt && (
									<Typography>
										La prochaine relance des signataires sera le{' '}
										<strong>{format(new Date(nextReminderAt), "dd/MM/yyyy 'à' HH:mm")}</strong>
									</Typography>
								)}
							</Alert>
						)}
					</Stack>
				</Stack>
			)}
		</Dialog>
	);
};

export default ProcedureConsultationDialog;
